<template>
  <v-expansion-panels>
    <v-expansion-panel>
      <v-expansion-panel-header class="title pa-4 custom-gray" data-cy="filterToggler">
        {{ $t('move.filters.title') }}
        <template v-slot:actions>
          <v-icon large>
            expand_more
          </v-icon>
        </template>
      </v-expansion-panel-header>
      <v-divider></v-divider>
      <v-expansion-panel-content eager data-cy="filterContent">
        <v-form
          ref="filterMovesForm">
          <v-row>
            <v-col lg="3" sm="6" cols="12">
              <v-text-field
                v-model="filter.name"
                :label="$t('move.properties.name')"
                @input="filterTextField"
                data-cy="name"
              />
            </v-col>
            <v-col lg="3" sm="6" cols="12">
              <v-text-field
                v-model="filter.transfereeName"
                :label="$t('move.properties.transferee')"
                @input="filterTextField"
                data-cy="transfereeName"
              />
            </v-col>
            <v-col lg="3" sm="6" cols="12">
              <v-text-field
                v-model="filter.relocationManagementCompany"
                :label="$t('move.properties.rmc')"
                @input="filterTextField"
                data-cy="relocationManagementCompany"
              />
            </v-col>
            <v-col lg="3" sm="6" cols="12">
              <v-dialog
                ref="dialogStartDate"
                v-model="modalStartDate"
                width="300px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="filter.startDate"
                    :label="$t('move.filters.moveActiveFrom')"
                    append-icon="calendar_today"
                    autocomplete="off"
                    v-on="on"
                    data-cy="startDate"
                  />
                </template>
                <v-date-picker
                  v-model="filter.startDate"
                  :allowed-dates="allowedMaxDate"
                  data-cy="startDatePicker"
                >
                  <v-spacer/>
                  <v-btn text color="primary" data-cy="clearFilterBtn" @click="clearDatePicker('startDate')">
                    {{ $t('common.form.clear') }}
                  </v-btn>
                  <v-btn text color="primary" data-cy="okFilterBtn" @click="filterDateField('startDate')">
                    {{ $t('common.form.ok') }}
                  </v-btn>
                </v-date-picker>
              </v-dialog>
            </v-col>
            <v-col lg="3" sm="6" cols="12">
              <v-dialog
                ref="dialogEndDate"
                v-model="modalEndDate"
                width="300px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="filter.plannedDeliveryDate"
                    :label="$t('move.filters.moveActiveTo')"
                    append-icon="calendar_today"
                    autocomplete="off"
                    v-on="on"
                    data-cy="plannedDeliveryDate"
                  />
                </template>
                <v-date-picker
                  v-model="filter.plannedDeliveryDate"
                  :allowed-dates="allowedMinDate"
                  data-cy="endDatePicker"
                >
                  <v-spacer/>
                  <v-btn text color="primary" data-cy="clearFilterBtn" @click="clearDatePicker('endDate')">
                    {{$t('common.form.clear')}}
                  </v-btn>
                  <v-btn text color="primary" data-cy="okFilterBtn" @click="filterDateField('endDate')">
                    {{$t('common.form.ok')}}
                  </v-btn>
                </v-date-picker>
              </v-dialog>
            </v-col>
            <v-col lg="3" sm="6" cols="12">
              <v-text-field
                v-model="filter.origin"
                :label="$t('move.properties.origin')"
                @input="filterTextField"
                data-cy="origin"
              />
            </v-col>
            <v-col lg="3" sm="6" cols="12">
              <v-text-field
                v-model="filter.destination"
                :label="$t('move.properties.destination')"
                @input="filterTextField"
                data-cy="destination"
              />
            </v-col>
            <v-col lg="3" sm="6" cols="12">
              <v-text-field
                v-model="filter.tracker"
                :label="$t('move.properties.tracker')"
                @input="filterTextField"
                data-cy="tracker"
              />
            </v-col>
            <v-col lg="3" sm="6" cols="12" data-cy="moveStatusCol">
              <v-select
                :items="moveStatuses"
                v-model="filter.moveStatus"
                :label="$t('move.properties.status')"
                @change="filterByStatus"
                :clearable="true"
                data-cy="moveStatus"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" justify="end" align="end">
              <v-btn @click="resetFilter" color="default" data-cy="resetMoveFilters">
                <v-icon small>
                  refresh
                </v-icon>
                {{ $t('common.actions.reset') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
  import {
    MOVE_STATUS_TYPE_ARR,
  } from '@/constants';
  import * as actions from '@/store/actions.type';
  import {mapActions} from 'vuex';
  import _ from 'lodash';

  export default {
    name: 'MovesFilter',
    data: () => ({
      filter: {
        name: null,
        transfereeName: null,
        relocationManagementCompany: null,
        startDate: null,
        plannedDeliveryDate: null,
        moveStatus: '0',
        origin: null,
        destination: null,
        tracker: null,
      },
      modalStartDate: false,
      modalEndDate: false,
      filterTextField: undefined,
    }),
    computed: {
      moveStatuses() {
        let statuses = [];
        for (let key in MOVE_STATUS_TYPE_ARR) {
          if (MOVE_STATUS_TYPE_ARR.hasOwnProperty(key)) {
            statuses.push({
              value: key,
              text: this.$t(`move.type.${MOVE_STATUS_TYPE_ARR[key]}`),
            });
          }
        }
        return statuses;
      },
      filterParams() {
        let startDate, endDate;

        if (this.filter.startDate) {
          startDate = new Date(this.filter.startDate);
          startDate.setDate(startDate.getDate() + 1);
          startDate.setSeconds(startDate.getSeconds() - 1);
          startDate = startDate.toISOString();
        }

        if (this.filter.plannedDeliveryDate) {
          endDate = new Date(this.filter.plannedDeliveryDate);
          endDate.setDate(endDate.getDate() + 1);
          endDate.setSeconds(endDate.getSeconds() - 1);
          endDate = endDate.toISOString();
        }

        return {
          name: this.filter.name,
          transfereeName: this.filter.transfereeName,
          relocationManagementCompany: this.filter.relocationManagementCompany,
          movesFromDate: startDate,
          movesToDate: endDate,
          moveStatusType: this.filter.moveStatus,
          startingLocation: this.filter.origin,
          destination: this.filter.destination,
          deviceName: this.filter.tracker,
        };
      },
    },
    watch: {
      $route() {
        this.setFiltersFromUrlParams();
      }
    },
    created () {
      this.filterTextField = _.debounce(($event) => {
        this.fetchMovesAction(this.filterParams);
        this.$root.$emit('filterUpdate');
      }, 500)
    },
    mounted() {
      this.fetchMovesAction(this.filterParams);
      this.setFiltersFromUrlParams();
    },
    methods: {
      ...mapActions({
        fetchMovesAction: actions.FETCH_MOVES,
      }),
      filterDateField(value) {
        this.fetchMovesAction(this.filterParams);
        this.$root.$emit('filterUpdate');
        value === 'startDate' ? this.modalStartDate = false : this.modalEndDate = false;
      },
      filterByStatus(value) {
        this.filter.moveStatus = value;
        this.fetchMovesAction(this.filterParams);
        this.$root.$emit('filterUpdate');
      },
      allowedMaxDate(val) {
        return this.filter.plannedDeliveryDate ? new Date(this.filter.plannedDeliveryDate) > new Date(val) : true;
      },
      allowedMinDate(val) {
        return this.filter.startDate ? new Date(this.filter.startDate) < new Date(val) : true;
      },
      clearDatePicker(value) {
        if (value === 'startDate') {
          this.modalStartDate = false;
          this.filter.startDate = null;
        } else {
          this.modalEndDate = false;
          this.filter.plannedDeliveryDate = null;
        }

        this.fetchMovesAction(this.filterParams);
        this.$root.$emit('filterUpdate');
      },
      resetFilter() {
        this.$refs.filterMovesForm.reset();
      },
      setFiltersFromUrlParams() {
        if (this.$route.query.name) {
          this.resetFilter();
          this.filter.name = this.$route.query.name;
        }
      }
    },
  };
</script>

<style scoped>
  .custom-gray {
    color: rgba(0, 0, 0, 0.6);
  }
</style>
